import React, { useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { ThemeContext } from './useThemeContext';

type Theme = 'light' | 'dark';
export type Palette = 'emerald' | 'sunset';

const VALID_PALETTES: Palette[] = ['emerald', 'sunset'];

function readPalette(): Palette {
  const stored = localStorage.getItem('palette');
  return VALID_PALETTES.includes(stored as Palette) ? (stored as Palette) : 'sunset';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    return (localStorage.getItem('theme') as Theme) ?? 'light';
  });
  const [palette, setPaletteState] = useState<Palette>(readPalette);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-palette', palette);
    localStorage.setItem('palette', palette);
  }, [palette]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const setPalette = useCallback((p: Palette) => {
    setPaletteState(p);
  }, []);

  const value = useMemo(
    () => ({ theme, palette, toggleTheme, setPalette }),
    [theme, palette, toggleTheme, setPalette],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
